import { styled, Box, Typography, Button, ButtonProps } from '@mui/material';
import React, { useState } from 'react';

interface FlashpackButtonProps extends Omit<ButtonProps, 'variant'> {
  variant?: 'primary' | 'secondary' | 'white' | 'wizard';
  shouldOverrideTypography?: boolean;
  target?: '_blank' | '_self';
}

const FlashpackButton: React.FC<FlashpackButtonProps> = ({
  variant = 'primary',
  startIcon,
  endIcon,
  children,
  disabled,
  onClick,
  shouldOverrideTypography = false,
  sx,
  ...props
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const ButtonVariant = disabled
    ? ButtonDisabled
    : {
        primary: ButtonPrimary,
        secondary: ButtonSecondary,
        white: ButtonWhite,
        wizard: ButtonWizard,
      }[variant];

  const Content = (
    <ContentContainer>
      {startIcon}
      <>
        {shouldOverrideTypography ? (
          <>
            {/* Some buttons will need their own typography variant */}
            {/* so we avoid wrapping the children when the flag is passed. */}
            {children}
          </>
        ) : (
          <Typography variant={'Button'}>{children}</Typography>
        )}
      </>
      {endIcon}
    </ContentContainer>
  );

  return (
    <ButtonVariant
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(e) => {
        onClick?.(e);
        setIsHovered(false);
      }}
      disabled={disabled}
      disableRipple
      sx={{
        ...sx,
      }}
      {...props}
    >
      <AnimationContainer>
        <AnimatedContent
          content={Content}
          transform={isHovered ? 'translateY(-150%)' : 'translateY(0%)'}
        />
        {Content}
        <AnimatedContent
          content={Content}
          transform={isHovered ? 'translateY(0%)' : 'translateY(150%)'}
        />
      </AnimationContainer>
    </ButtonVariant>
  );
};

const ButtonBase = styled(Button)({
  borderRadius: '4px',
  padding: '8px 12px',
  boxSizing: 'border-box',
  position: 'relative',
}) as typeof Button;

const ButtonDisabled = styled(ButtonBase)(({ theme }) => ({
  backgroundColor: theme.palette.principal.grey50,
  border: `1px solid ${theme.palette.principal.grey50}`,
  color: theme.palette.principal.white,
})) as typeof Button;

const ButtonPrimary = styled(ButtonBase)(({ theme }) => ({
  backgroundColor: theme.palette.principal.black,
  border: `1px solid ${theme.palette.principal.black}`,
  color: theme.palette.principal.white,
  '&:hover': {
    backgroundColor: theme.palette.principal.black,
    border: `1px solid ${theme.palette.principal.black}`,
    color: theme.palette.principal.white,
    '::after': {
      content: `""`,
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      width: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      borderRadius: '4px',
      pointerEvents: 'none',
    },
  },
})) as typeof Button;

const ButtonSecondary = styled(ButtonBase)(({ theme }) => ({
  backgroundColor: 'transparent',
  border: `1px solid ${theme.palette.principal.grey50}`,
  '&:hover': {
    borderColor: theme.palette.principal.black,
  },
})) as typeof Button;

const ButtonWhite = styled(ButtonBase)(({ theme }) => ({
  backgroundColor: theme.palette.principal.white,
  border: `1px solid ${theme.palette.principal.white}`,
  color: theme.palette.principal.black,
})) as typeof Button;

const ButtonWizard = styled(ButtonBase)(({ theme }) => ({
  background: 'linear-gradient(44deg, #F6F3F9 0%, #FBF2E3 100%)',
  color: theme.palette.principal.black,
})) as typeof Button;

const AnimationContainer = styled(Box)({
  position: 'relative',
  overflow: 'hidden',
});

const ContentContainer = styled(Box)({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  opacity: 0,
  zIndex: 2,
});

const AnimatedContent: React.FC<{
  content: React.ReactElement;
  transform: string;
}> = ({ content, transform }) =>
  React.cloneElement(content, {
    style: {
      opacity: 1,
      transform,
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      width: '100%',
      transition: 'transform 0.5s ease-in-out',
    },
  });

export default FlashpackButton;
